import React from 'react'
import {
  Tr,
  Td,
  Text,
  VStack,
  Code,
  Flex,
  Button,
  useColorMode,
} from '@chakra-ui/react'
import type { DataFeature } from '../types'

interface FeaturesDisplayProps {
  label: string
  features?: DataFeature[]
  codeBg: string
  onViewAll: (title: string, features: DataFeature[]) => void
  previewCount?: number
}

const FeaturesDisplay: React.FC<FeaturesDisplayProps> = ({
  label,
  features,
  codeBg,
  onViewAll,
  previewCount = 3,
}) => {
  const { colorMode } = useColorMode()

  if (!features || features.length === 0) {
    return (
      <Tr>
        <Td fontWeight="bold" verticalAlign="top">
          {label}
        </Td>
        <Td>
          <Text color="gray.500">-</Text>
        </Td>
      </Tr>
    )
  }

  const previewData = features.slice(0, previewCount)
  const hasMore = features.length > previewCount

  return (
    <Tr>
      <Td fontWeight="bold" verticalAlign="top">
        {label}
      </Td>
      <Td>
        <VStack align="stretch" spacing={2}>
          <Code
            fontSize="xs"
            bg={codeBg}
            p={2}
            borderRadius="md"
            whiteSpace="nowrap"
            overflowX="auto"
            maxW="100%"
            css={{
              '&::-webkit-scrollbar': {
                height: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background:
                  colorMode === 'light'
                    ? 'rgba(0,0,0,0.05)'
                    : 'rgba(255,255,255,0.05)',
              },
              '&::-webkit-scrollbar-thumb': {
                background:
                  colorMode === 'light'
                    ? 'rgba(0,0,0,0.2)'
                    : 'rgba(255,255,255,0.2)',
                borderRadius: '4px',
              },
            }}
          >
            {JSON.stringify(previewData)}
          </Code>
          {hasMore && (
            <Flex gap={2} align="center">
              <Text fontSize="sm" color="gray.500">
                ...{features.length - previewCount} more items
              </Text>
              <Button
                size="xs"
                colorScheme="blue"
                variant="link"
                onClick={() => onViewAll(label, features)}
              >
                View All
              </Button>
            </Flex>
          )}
        </VStack>
      </Td>
    </Tr>
  )
}

export default FeaturesDisplay
